import { Link } from 'react-router-dom';
import { Card } from '@thegpvc/ui';
import { text, cx } from '../../styles/theme';
import { formatMultiplier, formatUsd } from '../../utils/format';
import type { SpendSummary, TimePeriod } from '../../types/api';

const PERIOD_LABEL: Record<string, string> = {
  today: 'today',
  '7days': 'last 7 days',
  '30days': 'last 30 days',
  all: 'all time',
};

export function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="h-2 w-2 rounded-full bg-orange-500" />
      <span className={cx('text-xs font-medium uppercase tracking-wider', text.muted)}>{children}</span>
    </div>
  );
}

/**
 * WorkDeliveredCard answers "what did I get?": the API-equivalent value of
 * the period's agent output (subscription framing) or real spend (api
 * framing), with the recoverable-waste hook underneath.
 */
export function WorkDeliveredCard({ data, period, loading }: { data?: SpendSummary; period: TimePeriod; loading: boolean }) {
  const subscription = data?.billingMode === 'subscription';
  const current = data?.currentUsd ?? 0;
  const apiCurrent = data?.apiCurrentUsd ?? 0;
  const headline = subscription ? Math.max(0, current - apiCurrent) : current;
  const fee = data?.planMonthlyFeeUsd ?? 0;
  const recoverable = data?.recoverableUsd ?? 0;
  const unpricedModels = data?.unpricedModels ?? 0;

  let caption: React.ReactNode = null;
  if (subscription) {
    caption = (
      <>
        API-equivalent value of what your agents produced
        {fee > 0 && (
          <>
            {' '}— <span className={cx('font-semibold', text.primary)}>{formatMultiplier(headline / fee)} your plan fee</span>
          </>
        )}
        .
      </>
    );
  } else {
    caption = <>What your agents cost at API list prices.</>;
  }

  return (
    <Card className="h-full">
      <Card.Body className="h-full">
        <div className="flex h-full flex-col justify-between gap-4">
          <div>
            <Eyebrow>Work delivered</Eyebrow>
            <div className="mt-3 flex items-baseline gap-3">
              <span className={cx('text-5xl font-bold tabular-nums leading-none', text.primary)}>
                {loading ? '…' : formatUsd(headline)}
              </span>
              <span className={cx('text-sm', text.muted)}>{PERIOD_LABEL[period] ?? 'this period'}</span>
            </div>
            <p className={cx('mt-2 text-sm', text.secondary)}>{caption}</p>
            {unpricedModels > 0 && (
              <span
                className="mt-2 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300"
                title="Some models have no list price configured, so their spend is counted as $0."
              >
                {unpricedModels} model{unpricedModels === 1 ? '' : 's'} unpriced — cost understated
              </span>
            )}
            {subscription && apiCurrent > 0 && (
              <p className={cx('mt-1 text-sm', text.secondary)}>+ {formatUsd(apiCurrent)} API-key spend.</p>
            )}
          </div>
          {recoverable > 0 && (
            <div>
              <Link to="/stats?view=usage" className={cx('text-sm font-medium', text.link)}>
                {formatUsd(recoverable)} recoverable →
              </Link>
              <p className={cx('text-sm', text.muted)}>Loops and repeat re-reads.</p>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
